import { loadGsap } from './gsap-loader.js';

const GROUPS = ['style', 'colour'];

/**
 * Search results with instant filters and sorting. Every product is already on the page (up to 250 results), so
 * filtering is just showing and hiding cards. The filter state lives in the URL so links, refresh and the back
 * button all keep it.
 */
class SearchPageComponent extends HTMLElement {
  #abort = new AbortController();
  /** @type {any} */
  #gsap = null;
  #reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  #data = [];
  #first = true;
  #state = { style: new Set(), colour: new Set(), min: null, max: null, stock: false, sort: 'relevance' };

  connectedCallback() {
    this.grid = this.querySelector('[data-grid]');
    if (!this.grid) return;

    this.#data = [...this.querySelectorAll('[data-card]')].map((el) => ({
      el,
      index: Number(el.dataset.index),
      title: (el.dataset.title || '').toLowerCase(),
      price: Number(el.dataset.price) || 0,
      available: el.dataset.available === 'true',
      published: Number(el.dataset.published) || 0,
      tags: new Set((el.dataset.tags || '').split(' ').filter(Boolean)),
    }));

    this.els = {
      summary: this.querySelector('[data-summary]'),
      pills: this.querySelector('[data-pills]'),
      none: this.querySelector('[data-none]'),
      count: this.querySelector('[data-count]'),
      countWord: this.querySelector('[data-count-word]'),
      visibleCount: this.querySelector('[data-visible-count]'),
      activeCount: this.querySelector('[data-active-count]'),
      min: this.querySelector('[data-price-min]'),
      max: this.querySelector('[data-price-max]'),
      stock: this.querySelector('[data-stock]'),
      stockCount: this.querySelector('[data-stock-count]'),
      sort: this.querySelector('[data-sort]'),
      filters: this.querySelector('[data-filters]'),
    };

    // Placeholders show the real price range of these results.
    const prices = this.#data.map((d) => d.price / 100);
    if (this.els.min) this.els.min.placeholder = String(Math.floor(Math.min(...prices)));
    if (this.els.max) this.els.max.placeholder = String(Math.ceil(Math.max(...prices)));

    this.#readUrl();
    this.#syncInputs();
    this.#bind();
    this.#render();
    loadGsap().then((gsap) => (this.#gsap = gsap));
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#unlockScroll();
  }

  /* ---------- state <-> url ---------- */

  #readUrl() {
    const params = new URLSearchParams(window.location.search);
    const list = (key) => (params.get(key) || '').split(',').filter(Boolean);
    this.#state.style = new Set(list('style'));
    this.#state.colour = new Set(list('colour'));
    const number = (key) => (params.get(key) !== null && params.get(key) !== '' && !Number.isNaN(Number(params.get(key))) ? Number(params.get(key)) : null);
    this.#state.min = number('min');
    this.#state.max = number('max');
    this.#state.stock = params.get('stock') === '1';
    this.#state.sort = ['relevance', 'price-asc', 'price-desc', 'title', 'newest'].includes(params.get('sort')) ? params.get('sort') : 'relevance';
  }

  #writeUrl() {
    const url = new URL(window.location.href);
    const set = (key, value) => (value ? url.searchParams.set(key, value) : url.searchParams.delete(key));
    set('style', [...this.#state.style].join(','));
    set('colour', [...this.#state.colour].join(','));
    set('min', this.#state.min ?? '');
    set('max', this.#state.max ?? '');
    set('stock', this.#state.stock ? '1' : '');
    set('sort', this.#state.sort !== 'relevance' ? this.#state.sort : '');
    window.history.replaceState({}, '', url);
  }

  #syncInputs() {
    this.querySelectorAll('[data-filter]').forEach((input) => {
      input.checked = this.#state[input.dataset.filter].has(input.value);
    });
    if (this.els.min) this.els.min.value = this.#state.min ?? '';
    if (this.els.max) this.els.max.value = this.#state.max ?? '';
    if (this.els.stock) this.els.stock.checked = this.#state.stock;
    if (this.els.sort) this.els.sort.value = this.#state.sort;
  }

  /* ---------- events ---------- */

  #bind() {
    const { signal } = this.#abort;
    const on = (target, type, handler) => target?.addEventListener(type, handler, { signal });

    this.querySelectorAll('[data-filter]').forEach((input) =>
      on(input, 'change', () => {
        const set = this.#state[input.dataset.filter];
        if (input.checked) set.add(input.value);
        else set.delete(input.value);
        this.#render(true);
      })
    );
    const price = (input, key) =>
      on(input, 'input', () => {
        this.#state[key] = input.value === '' ? null : Math.max(0, Number(input.value));
        this.#render(true);
      });
    price(this.els.min, 'min');
    price(this.els.max, 'max');
    on(this.els.stock, 'change', () => {
      this.#state.stock = this.els.stock.checked;
      this.#render(true);
    });
    on(this.els.sort, 'change', () => {
      this.#state.sort = this.els.sort.value;
      this.#render(true);
    });
    this.querySelectorAll('[data-clear]').forEach((button) => on(button, 'click', () => this.#clear()));
    on(this.els.pills, 'click', (event) => {
      const pill = event.target.closest('[data-pill]');
      if (!pill) return;
      this.#removePill(pill.dataset.pill, pill.dataset.value);
    });

    this.querySelectorAll('[data-open-filters]').forEach((button) => on(button, 'click', () => this.#openFilters()));
    this.querySelectorAll('[data-close-filters]').forEach((button) => on(button, 'click', () => this.#closeFilters()));
    on(this.querySelector('[data-scrim]'), 'click', () => this.#closeFilters());
    on(document, 'keydown', (event) => {
      if (event.key === 'Escape') this.#closeFilters();
    });
  }

  #clear() {
    this.#state = { style: new Set(), colour: new Set(), min: null, max: null, stock: false, sort: this.#state.sort };
    this.#syncInputs();
    this.#render(true);
  }

  #removePill(kind, value) {
    if (kind === 'style' || kind === 'colour') this.#state[kind].delete(value);
    else if (kind === 'min') this.#state.min = null;
    else if (kind === 'max') this.#state.max = null;
    else if (kind === 'stock') this.#state.stock = false;
    this.#syncInputs();
    this.#render(true);
  }

  /* ---------- filtering ---------- */

  /** Does a product pass every filter, optionally ignoring one group (used for the live option counts)? */
  #passes(item, ignore) {
    const s = this.#state;
    for (const group of GROUPS) {
      if (group === ignore || s[group].size === 0) continue;
      if (![...s[group]].some((handle) => item.tags.has(handle))) return false;
    }
    const price = item.price / 100;
    if (ignore !== 'price') {
      if (s.min !== null && price < s.min) return false;
      if (s.max !== null && price > s.max) return false;
    }
    if (ignore !== 'stock' && s.stock && !item.available) return false;
    return true;
  }

  #sorted(items) {
    const by = {
      relevance: (a, b) => a.index - b.index,
      'price-asc': (a, b) => a.price - b.price || a.index - b.index,
      'price-desc': (a, b) => b.price - a.price || a.index - b.index,
      title: (a, b) => a.title.localeCompare(b.title),
      newest: (a, b) => b.published - a.published || a.index - b.index,
    };
    return [...items].sort(by[this.#state.sort]);
  }

  #render(animate = false) {
    const visible = this.#data.filter((item) => this.#passes(item));
    const shown = new Set(visible);
    this.#data.forEach((item) => (item.el.hidden = !shown.has(item)));

    // Reorder the real DOM so keyboard and screen-reader order matches what's on screen.
    this.#sorted(visible).forEach((item) => this.grid.append(item.el));

    this.#renderCounts();
    this.#renderPills();

    const total = this.#data.length;
    const filtered = visible.length !== total;
    this.els.summary.textContent = filtered ? `${visible.length} of ${total} works` : `${total} works`;
    if (this.els.count) this.els.count.textContent = String(visible.length);
    if (this.els.countWord) this.els.countWord.textContent = visible.length === 1 ? 'result' : 'results';
    if (this.els.visibleCount) this.els.visibleCount.textContent = String(visible.length);
    this.els.none.hidden = visible.length > 0;
    this.grid.hidden = visible.length === 0;

    this.#writeUrl();
    if (animate) this.#animate(visible);
  }

  /** Each option's count answers "how many would I get if I picked this?", so groups ignore themselves. */
  #renderCounts() {
    GROUPS.forEach((group) => {
      this.querySelectorAll(`[data-group="${group}"] [data-option]`).forEach((option) => {
        const handle = option.dataset.option;
        const count = this.#data.filter((item) => this.#passes(item, group) && item.tags.has(handle)).length;
        const checked = this.#state[group].has(handle);
        option.querySelector('[data-option-count]').textContent = String(count);
        option.classList.toggle('is-empty', count === 0 && !checked);
        const everywhere = this.#data.some((item) => item.tags.has(handle));
        option.hidden = !everywhere;
      });
      // A filter with only one option isn't a filter (e.g. Style on a Strokes-only collection).
      const fieldset = this.querySelector(`[data-group="${group}"]`);
      if (fieldset) fieldset.hidden = fieldset.querySelectorAll('[data-option]:not([hidden])').length < 2;
    });
    if (this.els.stockCount) {
      this.els.stockCount.textContent = String(this.#data.filter((item) => this.#passes(item, 'stock') && item.available).length);
    }
  }

  #renderPills() {
    const pills = [];
    const label = (group, handle) =>
      this.querySelector(`[data-group="${group}"] [data-option="${CSS.escape(handle)}"] .sp-check__label, [data-group="${group}"] [data-option="${CSS.escape(handle)}"] .sp-colour__name`)?.textContent.trim() ?? handle;
    GROUPS.forEach((group) => this.#state[group].forEach((handle) => pills.push({ kind: group, value: handle, text: label(group, handle) })));
    if (this.#state.min !== null) pills.push({ kind: 'min', value: '', text: `From ${this.#state.min}` });
    if (this.#state.max !== null) pills.push({ kind: 'max', value: '', text: `To ${this.#state.max}` });
    if (this.#state.stock) pills.push({ kind: 'stock', value: '', text: 'In stock' });

    this.els.pills.hidden = pills.length === 0;
    this.els.pills.innerHTML = pills.length
      ? pills
          .map(
            (pill) =>
              `<button type="button" class="sp-pill" data-pill="${pill.kind}" data-value="${pill.value}" aria-label="Remove filter ${pill.text}">${pill.text}<span aria-hidden="true">×</span></button>`
          )
          .join('') + '<button type="button" class="sp-pill sp-pill--clear" data-clear>Clear all</button>'
      : '';
    this.els.pills.querySelector('[data-clear]')?.addEventListener('click', () => this.#clear(), { signal: this.#abort.signal });
    if (this.els.activeCount) this.els.activeCount.textContent = pills.length ? `(${pills.length})` : '';
  }

  #animate(visible) {
    if (!this.#gsap || this.#reduceMotion) return;
    const cards = visible.slice(0, 24).map((item) => item.el);
    this.#gsap.fromTo(
      cards,
      { opacity: 0, y: 14 },
      { opacity: 1, y: 0, duration: 0.5, ease: 'power3.out', stagger: 0.015, clearProps: 'opacity,transform', overwrite: 'auto' }
    );
  }

  /* ---------- mobile filter drawer ---------- */

  #openFilters() {
    this.classList.add('is-filters-open');
    this.#lockScroll();
    this.querySelector('.sp-close')?.focus({ preventScroll: true });
  }

  #closeFilters() {
    if (!this.classList.contains('is-filters-open')) return;
    this.classList.remove('is-filters-open');
    this.#unlockScroll();
    this.querySelector('[data-open-filters]')?.focus({ preventScroll: true });
  }

  #lockScroll() {
    window.lenis?.stop?.();
    document.documentElement.style.overflow = 'hidden';
  }

  #unlockScroll() {
    window.lenis?.start?.();
    document.documentElement.style.overflow = '';
  }
}

if (!customElements.get('search-page-component')) {
  customElements.define('search-page-component', SearchPageComponent);
}
